/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useMohSourcesList.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Custom hook for managing MOH Sources list.
    Integrates with useDataGrid for pagination, sorting, and search.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useCallback, useEffect } from 'react';
import { useDataGrid, DataGridColumn } from '../../../hooks/useDataGrid';
import { useContextStore } from '../../../store/context';
import { fetchMohSources } from '../api';
import { MOHSource, MOHSourceFilters } from '../types';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';

interface UseMohSourcesListProps {
  initialColumns?: DataGridColumn<MOHSource>[];
  filters?: MOHSourceFilters;
  domainId?: number;
  onRefreshNeeded?: () => void;
}

/**
 * Custom React hook for managing the MOH Sources DataGrid list.
 * Integrates directly with `useDataGrid` for pagination, sorting, and search capabilities.
 * Supports Domain Contextual Filtering to only show MOH sources belonging to the current tenant.
 * 
 * @param props - Configuration properties for the list hook.
 * @returns Combined DataGrid state and the effective Domain ID applied to the filter.
 */
export function useMohSourcesList(props: UseMohSourcesListProps = {}) {
  const { showError } = useNotifications();
  const { t } = useI18n();
  const { selectedDomain } = useContextStore();

  // Use provided domainId or fallback to selected domain
  const effectiveDomainId = props.domainId ?? selectedDomain?.id;

  const fetchData = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      try {
        const result = await fetchMohSources(params, props.filters, effectiveDomainId);
        return {
          data: result.data || [],
          pagination: result.pagination || {
            page: 1,
            limit: 25,
            total: 0,
            totalPages: 1,
          },
        };
      } catch (error) {
        showError(
          error instanceof Error
            ? error.message
            : t('mohSources.errors.loadFailed') || 'Failed to load MOH sources',
        );
        return {
          data: [] as MOHSource[],
          pagination: { page: 1, limit: 25, total: 0, totalPages: 0 },
        };
      }
    },
    [props.filters, effectiveDomainId, showError, t],
  );

  const dataGridResult = useDataGrid({
    fetchData,
    initialColumns: props.initialColumns,
    initialSortBy: 'created_at',
    initialSortOrder: 'desc',
    initialLimit: 10,
    moduleName: 'mohSources',
    reloadKey: effectiveDomainId ?? null,
  });

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (effectiveDomainId !== undefined && props.onRefreshNeeded) {
      props.onRefreshNeeded();
    }
  }, [effectiveDomainId]);

  return {
    ...dataGridResult,
    effectiveDomainId,
  };
}

